/**
 * Mechitza-aware seating (docs/GUEST-EXPERIENCE-REDESIGN.md, Phase 9): the
 * office marks tables as the men's or the women's section, and auto-assign
 * seats each party's men and women on their own side. A table with no section
 * is mixed — anyone may sit there.
 *
 * The section is stored on the table itself (`config.section`, like its chair
 * type), so every plan of an event lays its sections out on its own.
 */
/** A table's section. A table with none is mixed. */
export const SEATING_SECTIONS = ['MEN', 'WOMEN'];
export const SEATING_SECTION_LABELS = {
    MEN: "Men's section",
    WOMEN: "Women's section",
};
/** A named adult's gender, which decides their side of the mechitza. */
export const PERSON_GENDERS = ['MALE', 'FEMALE'];
export const PERSON_GENDER_LABELS = {
    MALE: 'Man',
    FEMALE: 'Woman',
};
/** Where children sit when the plan is seated by section. */
export const CHILDREN_SEATING = ['WOMEN', 'MEN', 'ANY'];
export const CHILDREN_SEATING_LABELS = {
    WOMEN: 'With the women',
    MEN: 'With the men',
    ANY: 'Wherever there is room',
};
/** Who a seat is for: a man, a woman or a child of the party. */
export const SEAT_KINDS = ['MALE', 'FEMALE', 'CHILD'];
const isSection = (value) => typeof value === 'string' && SEATING_SECTIONS.includes(value);
/** The section a table's stored config names, or null for a mixed table. */
export function tableSectionOf(config) {
    if (!config || typeof config !== 'object')
        return null;
    const section = config.section;
    return isSection(section) ? section : null;
}
/** The section a person of this kind sits in; null when either side will do. */
export function sectionFor(kind, childrenWith) {
    if (kind === 'MALE')
        return 'MEN';
    if (kind === 'FEMALE')
        return 'WOMEN';
    return childrenWith === 'ANY' ? null : childrenWith;
}
/**
 * What kind of person a named member is: a child by their type, an adult by
 * their gender — null when nobody has said, since guessing would put a name
 * on the wrong side of the mechitza.
 */
export function memberSeatKind(member) {
    if (member.memberType === 'CHILD')
        return 'CHILD';
    if (member.gender === 'MALE' || member.gender === 'FEMALE')
        return member.gender;
    return null;
}
/** Whether a person of this kind may sit at a table of this section. */
export function kindFitsSection(kind, section) {
    if (section === null || kind === 'CHILD')
        return true;
    return section === (kind === 'MALE' ? 'MEN' : 'WOMEN');
}
/**
 * How many of a party sit on the wrong side, from its head count and the seats
 * it holds in each section. Children may sit on either side, so they are never
 * counted against it: a party of one man, one woman and a child holding two
 * seats in the women's section is right; holding three is a man among them.
 * A party whose head count says nothing (no men, women or children) is never
 * flagged.
 */
export function sectionMismatch(party, held) {
    const known = party.maleCount + party.femaleCount + party.kidCount;
    if (known === 0)
        return { menInWomen: 0, womenInMen: 0 };
    return {
        womenInMen: Math.max(0, held.men - party.maleCount - party.kidCount),
        menInWomen: Math.max(0, held.women - party.femaleCount - party.kidCount),
    };
}
//# sourceMappingURL=mechitza.js.map